import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function DisbursementHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [disbursements, setDisbursements] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchDisbursements(); }, [page, filters]);

  const fetchDisbursements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const res = await api.get(`/loans/disbursements?${params}`);
      setDisbursements(res.data.data.disbursements);
      setStats(res.data.data.stats);
      setTotalPages(res.data.data.pagination.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (p) => {
    if (!p) return '';
    let f = p.replace(/\s/g, '');
    if (f.startsWith('254')) return '0' + f.substring(3);
    return f;
  };

  const getStatusColor = (s) => ({ initiated: '#3b82f6', processing: '#f59e0b', success: '#10b981', failed: '#ef4444', cancelled: '#6b7280' }[s] || '#6b7280');
  const getStatusBg = (s) => ({ initiated: '#eff6ff', processing: '#fffbeb', success: '#ecfdf5', failed: '#fef2f2', cancelled: '#f9fafb' }[s] || '#f9fafb');

  const handleRetry = async (id) => {
    if (!confirm('Retry this disbursement?')) return;
    try {
      const res = await api.post(`/loans/disbursements/${id}/retry`);
      alert(res.data.message);
      fetchDisbursements();
    } catch (err) {
      alert(err.response?.data?.message || 'Retry failed');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3>Disbursement History</h3>
        <button className="btn btn-outline" onClick={() => navigate('/loans/applications')}>← Back to Applications</button>
      </div>

      {stats && (
        <div className="grid grid-5 gap-1 mb-3">
          <div className="card p-2 text-center" style={{ background: '#f0f9ff', borderLeft: '4px solid #3b82f6' }}>
            <div className="text-xs text-muted">Total</div>
            <div className="font-bold text-lg">{stats.total}</div>
            <div className="text-xs text-muted">KES {(stats.totalAmount || 0).toLocaleString()}</div>
          </div>
          <div className="card p-2 text-center" style={{ background: '#ecfdf5', borderLeft: '4px solid #10b981' }}>
            <div className="text-xs text-muted">Successful</div>
            <div className="font-bold text-lg text-success">{stats.successful}</div>
            <div className="text-xs text-muted">KES {(stats.successfulAmount || 0).toLocaleString()}</div>
          </div>
          <div className="card p-2 text-center" style={{ background: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
            <div className="text-xs text-muted">Failed</div>
            <div className="font-bold text-lg text-danger">{stats.failed}</div>
          </div>
          <div className="card p-2 text-center" style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
            <div className="text-xs text-muted">Pending</div>
            <div className="font-bold text-lg" style={{ color: '#f59e0b' }}>{stats.pending}</div>
          </div>
          <div className="card p-2 text-center" style={{ background: '#f0fdf4', borderLeft: '4px solid #22c55e' }}>
            <div className="text-xs text-muted">Success Rate</div>
            <div className="font-bold text-lg text-success">{stats.total > 0 ? Math.round((stats.successful / stats.total) * 100) : 0}%</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-2 mb-3">
        <div className="grid grid-5 gap-1">
          <div className="form-group">
            <label className="text-xs">Status</label>
            <select className="form-control" value={filters.status} onChange={e => { setFilters(p => ({ ...p, status: e.target.value })); setPage(1); }}>
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="processing">Processing</option>
              <option value="initiated">Initiated</option>
            </select>
          </div>
          <div className="form-group">
            <label className="text-xs">Search</label>
            <input className="form-control" placeholder="Name, phone, receipt..." value={filters.search} onChange={e => { setFilters(p => ({ ...p, search: e.target.value })); setPage(1); }} />
          </div>
          <div className="form-group">
            <label className="text-xs">From</label>
            <input type="date" className="form-control" value={filters.startDate} onChange={e => { setFilters(p => ({ ...p, startDate: e.target.value })); setPage(1); }} />
          </div>
          <div className="form-group">
            <label className="text-xs">To</label>
            <input type="date" className="form-control" value={filters.endDate} onChange={e => { setFilters(p => ({ ...p, endDate: e.target.value })); setPage(1); }} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-outline btn-sm w-full" onClick={() => { setFilters({ status: '', search: '', startDate: '', endDate: '' }); setPage(1); }}>Clear</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '12px', textAlign: 'left' }}>Date/Time</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Client</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Phone</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Receipt</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Disbursed By</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center p-5">Loading...</td></tr>
            ) : disbursements.length === 0 ? (
              <tr><td colSpan={8} className="text-center p-5 text-muted">No disbursements found</td></tr>
            ) : disbursements.map((d) => (
              <React.Fragment key={d._id}>
                <tr style={{ borderBottom: '1px solid #f1f5f9', background: expandedId === d._id ? '#f8fafc' : '#fff', cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === d._id ? null : d._id)}>
                  <td style={{ padding: '12px' }}>
                    <div>{new Date(d.initiatedAt).toLocaleDateString()}</div>
                    <div className="text-xs text-muted">{new Date(d.initiatedAt).toLocaleTimeString()}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div className="font-bold">{d.clientName || 'N/A'}</div>
                    <div className="text-xs text-muted">{d.clientNumber || ''}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>{formatPhone(d.disbursementPhone)}</div>
                    {d.disbursementPhone !== d.clientPhone && <div className="text-xs text-danger">⚡ Different</div>}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>KES {d.disbursementAmount?.toLocaleString()}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {d.mpesaReceipt ? (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', background: '#ecfdf5', padding: '2px 8px', borderRadius: '4px', color: '#065f46' }}>{d.mpesaReceipt}</span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, color: getStatusColor(d.status), background: getStatusBg(d.status), border: `1px solid ${getStatusColor(d.status)}` }}>
                      {d.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div className="font-bold text-sm">{d.initiatedBy?.name || 'N/A'}</div>
                    <div className="text-xs text-muted">{d.initiatedBy?.role || ''}</div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div className="flex gap-1" style={{ justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                      {d.application?._id && (
                        <button className="btn btn-sm btn-outline" onClick={() => navigate(`/loans/applications/${d.application._id}/review`)} title="View Application">👁️</button>
                      )}
                      {d.status === 'failed' && d.canRetry && (
                        <button className="btn btn-sm btn-warning" onClick={() => handleRetry(d._id)} title="Retry">🔄</button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedId === d._id && (
                  <tr style={{ background: '#f8fafc' }}>
                    <td colSpan={8} style={{ padding: '16px 24px' }}>
                      <div className="grid grid-3 gap-2">
                        <div>
                          <div className="text-xs text-muted font-bold mb-1">Transaction Details</div>
                          <div className="text-sm"><strong>Transaction ID:</strong> {d.transactionId || d.mpesaReceipt || 'N/A'}</div>
                          <div className="text-sm"><strong>Conversation ID:</strong> {d.conversationId || 'N/A'}</div>
                          <div className="text-sm"><strong>Result Code:</strong> {d.resultCode || 'N/A'}</div>
                          <div className="text-sm"><strong>Result Desc:</strong> {d.resultDesc || 'N/A'}</div>
                          {d.transactionDate && <div className="text-sm"><strong>M-Pesa Time:</strong> {d.transactionDate}</div>}
                        </div>
                        <div>
                          <div className="text-xs text-muted font-bold mb-1">Client Details</div>
                          <div className="text-sm"><strong>Name:</strong> {d.clientName}</div>
                          <div className="text-sm"><strong>Client #:</strong> {d.clientNumber}</div>
                          <div className="text-sm"><strong>National ID:</strong> {d.clientNationalId || 'N/A'}</div>
                          <div className="text-sm"><strong>Registered Phone:</strong> {formatPhone(d.clientPhone)}</div>
                          <div className="text-sm"><strong>Disbursement Phone:</strong> {formatPhone(d.disbursementPhone)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted font-bold mb-1">Disbursement Info</div>
                          <div className="text-sm"><strong>Product:</strong> {d.loanProduct || 'N/A'}</div>
                          <div className="text-sm"><strong>Duration (Days):</strong> {d.duration || 'N/A'}</div>
                          <div className="text-sm"><strong>Interest:</strong> {d.interestRate || 'N/A'}%</div>
                          <div className="text-sm"><strong>Initiated:</strong> {new Date(d.initiatedAt).toLocaleString()}</div>
                          <div className="text-sm"><strong>Completed:</strong> {d.completedAt ? new Date(d.completedAt).toLocaleString() : 'N/A'}</div>
                          <div className="text-sm"><strong>Retries:</strong> {d.retryCount || 0}</div>
                        </div>
                      </div>
                      {d.verificationNotes && (
                        <div className="mt-2" style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <div className="text-xs text-muted font-bold">Verification Notes</div>
                          <div className="text-sm">{d.verificationNotes}</div>
                        </div>
                      )}
                      {d.notes && (
                        <div className="mt-1" style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <div className="text-xs text-muted font-bold">Notes</div>
                          <div className="text-sm">{d.notes}</div>
                        </div>
                      )}
                      <div className="mt-2" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {Object.entries(d.verifiedDetails || {}).map(([key, val]) => (
                          <span key={key} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: val ? '#ecfdf5' : '#fef2f2', color: val ? '#059669' : '#dc2626', border: `1px solid ${val ? '#10b981' : '#fecaca'}` }}>
                            {val ? '✅' : '❌'} {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-muted">
                        <strong>Disbursed by:</strong> {d.initiatorName} ({d.initiatorRole}) — {d.initiatorEmail}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-2">
          <div className="text-sm text-muted">Page {page} of {totalPages}</div>
          <div className="flex gap-1">
            <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
            <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}